<template>
  <v-row no-gutters>
    <v-col cols="5" v-if="isFetched">
      <v-row no-gutters class="pb-5">
        <base-button
          :disabled="$attrs.disabled"
          @click="openAddStyleDialog"
          translation-path="button.addStyle"
          class="mr-0"
        ></base-button>
      </v-row>
      <v-row
        v-if="hasDefault"
        no-gutters
        class="border align-center element justify-space-between flex-nowrap px-4"
        style="background-color: white"
      >
        <v-col cols="10">
          <div
            class="text-left text-truncate text-body-2 flex-shrink-0 py-2"
            :class="$attrs.disabled ? 'text--disabled' : 'text--black'"
          >
            {{ $i18n.t('admin.visualizationDefault') }}
          </div>
        </v-col>
        <v-col cols="auto">
          <dots-menu
            :disabled="$attrs.disabled"
            font-size="19px"
            :items="defaultDotsItems"
            @manageStyle="openDefaultStyleDialog"
          >
          </dots-menu>
        </v-col>
      </v-row>
      <v-row
        no-gutters
        class="border align-center element justify-space-between flex-nowrap px-4"
        style="background-color: white"
        v-for="style in sortedStyles"
        :key="style.id"
      >
        <v-col cols="10">
          <div
            class="text-left text-truncate text-body-2 flex-shrink-0 py-2"
            :class="$attrs.disabled ? 'text--disabled' : 'text--black'"
          >
            {{ style.name }}
          </div>
        </v-col>
        <v-col cols="auto">
          <dots-menu
            :disabled="$attrs.disabled"
            font-size="19px"
            :items="dotsItems"
            @manageStyle="openManageStyleDialog(style.id)"
            @changeName="openEditNameDialog(style.id)"
            @delete="openDeleteDialog(style.id)"
          >
          </dots-menu>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="5" v-else>
      <main-loader></main-loader>
    </v-col>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteStyle')"
      :body="$i18n.t('dialog.body.deleteStyleConfirmation', { name })"
      @agree="handleStyleDelete"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
    <main-input-dialog
      v-if="isAddDialogLoaded"
      :inputs="addNewStyleInputs"
      :is-valid="isNameValid"
      :is-visible.sync="isAddDialogVisible"
      dialog-width="45%"
      :loading="isConfirmButtonLoading"
      :title-translation="$i18n.t('dialog.title.addDefaultStyle')"
      @agree="handleStyleAdd"
      agree-text="dialog.agreeSave"
      header-shadow
    />
    <main-dialog
      v-if="isEditNameDialogLoaded"
      :is-visible.sync="isEditNameDialogVisible"
      :title="$i18n.t('dialog.title.changeName')"
      @agree="handleStyleEditName"
      :loading="isConfirmButtonLoading"
      modal-width="45%"
      agree-text="dialog.agreeSave"
      header-shadow
      :is-valid="isNameValid"
      body-padding="16px"
      underlineFooter
      :is-buttons-right="false"
    >
      <template #body>
        <main-input-dialog-row
          name="name"
          translation-path="dialog.name"
          :data-type="dataType"
          clearable
          cols="12"
          v-model="name"
        ></main-input-dialog-row>
      </template>
    </main-dialog>
    <style-dialog
      v-if="isStyleDialogLoaded"
      :layer="currentLayerStyleEdit"
      :attributes="currentLayerStyleEditAttributes"
      :geomType="currentLayerStyleEditGeometryType"
      :is-visible.sync="isStyleDialogVisible"
      :loading="isStyleButtonLoading"
      @acceptStyle="saveStyle"
      scope="module"
      is-admin
    />
  </v-row>
</template>

<script>
import DotsMenu from '@/components/DotsMenu';
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import MainInputDialogRow from '@/components/MainInputDialogRow';

export default {
  name: 'PredefinedStyles',
  components: {
    MainInputDialogRow,
    MainLoader,
    DotsMenu,
    StyleDialog: () => import('@/components/StyleDialog'),
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    hasDefault: {
      type: Boolean,
      default: true,
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    name: '',
    dotsItems: [
      {
        name: 'manageStyle',
      },
      {
        name: 'changeName',
      },
      {
        name: 'delete',
      },
    ],
    defaultDotsItems: [
      {
        name: 'manageStyle',
      },
    ],
    dataType: { name: 'text' },
    isStyleDialogVisible: false,
    isStyleDialogLoaded: false,
    styleToEdit: undefined,
    styleToDelete: undefined,
    currentLayerStyleEdit: {},
    currentLayerStyleEditAttributes: [],
    currentLayerStyleEditGeometryType: '',
    isFetched: false,
    isConfirmButtonLoading: false,
    isStyleButtonLoading: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isAddDialogVisible: false,
    isAddDialogLoaded: false,
    isEditNameDialogLoaded: false,
    isEditNameDialogVisible: false,
    defaultStyleChanged: false,
  }),
  computed: {
    layer: get('layers/featuresLayers@:layerId'),
    styles: get('styles/styles@:dataSourceName'),
    sortedStyles() {
      return this.$_sortObjectsByAttribute([...this.styles], 'name');
    },
    isNameValid() {
      if (this.name === null) {
        return false;
      } else {
        return !(this.name.trim() === '' || this.name === undefined);
      }
    },
    addNewStyleInputs() {
      return [
        {
          name: 'name',
          hint: this.$i18n.t('dialog.writeDefaultStyleName'),
          rules: [v => !!v || 'rules.required'],
          translationPath: 'dialog.body.name',
          persistentHint: true,
          cols: '12',
          dataType: {
            name: 'text',
          },
        },
      ];
    },
  },
  methods: {
    getLayerMetadata: call('layers/getLayerMetadata'),
    editLayer: call('layers/editLayer'),
    getStyles: call('styles/getStyles'),
    getStyle: call('styles/getStyle'),
    addStyle: call('styles/addStyle'),
    editStyle: call('styles/editStyle'),
    deleteStyle: call('styles/deleteStyle'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),

    async saveStyle(style) {
      if (this.defaultStyleChanged) {
        this.defaultStyleChanged = false;
        this.isStyleButtonLoading = true;
        await this.editLayer({
          layer_id: this.layerId,
          layer_type: 'features_layers',
          body: {
            style_web: style.style,
          },
          scope: 'core',
        });
        await this.getFeaturesLayer(this.layerId);
        this.styleToEdit = undefined;
        this.isStyleButtonLoading = false;
      } else {
        try {
          this.isStyleButtonLoading = true;
          await this.editStyle({
            payload: {
              name: this.styleToEdit.name,
              style: style.style,
            },
            dataSourceName: this.dataSourceName,
            style_id: this.styleToEdit.id,
          });
          this.styleToEdit = undefined;
          this.isStyleButtonLoading = false;
        } catch (error) {
          console.log('handleBlockUser -> error', error);
          this.isStyleButtonLoading = false;
        }
      }

      this.isStyleDialogVisible = false;
    },
    async handleStyleEditName() {
      try {
        this.isConfirmButtonLoading = true;
        await this.editStyle({
          payload: {
            name: this.name,
          },
          dataSourceName: this.styleToEdit.datasource,
          style_id: this.styleToEdit.id,
        });
        this.styleToEdit = undefined;
        this.name = '';
        this.isConfirmButtonLoading = false;
        this.isEditNameDialogVisible = false;
      } catch (error) {
        console.log('handleBlockUser -> error', error);
        this.isEditNameDialogLoaded = false;
        this.styleToEdit = undefined;
        this.name = '';
        this.isConfirmButtonLoading = false;
        this.isEditNameDialogVisible = false;
      }
    },
    async openEditNameDialog(id) {
      this.styleToEdit = await this.getStyle({ dataSourceName: this.dataSourceName, style_id: id });
      this.name = this.styleToEdit.name;
      this.isEditNameDialogLoaded = true;
      this.isEditNameDialogVisible = true;
    },
    async handleStyleAdd() {
      this.isConfirmButtonLoading = true;
      try {
        const l = this.layer;
        await this.addStyle({
          payload: {
            name: this.addNewStyleInputs.find(input => input.name === 'name').value,
            style: l.style_web,
          },
          dataSourceName: this.dataSourceName,
        });
        this.isConfirmButtonLoading = false;
        this.name = '';
        this.isAddDialogLoaded = false;
        this.isAddDialogVisible = false;
      } catch (e) {
        this.isConfirmButtonLoading = false;
        this.name = '';
        this.isAddDialogLoaded = false;
        this.isAddDialogVisible = false;
      }
    },
    async handleStyleDelete() {
      this.isConfirmButtonLoading = true;
      await this.deleteStyle({ dataSourceName: this.dataSourceName, style_id: this.styleToDelete });
      this.styleToDelete = undefined;
      this.isConfirmButtonLoading = false;
      this.isDeleteDialogLoaded = false;
      this.isDeleteDialogVisible = false;
    },
    async openDefaultStyleDialog() {
      const l = this.layer;
      this.currentLayerStyleEditGeometryType = l.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(
        JSON.stringify({
          style: l.style_web,
          layer_id: l.id,
          style_qgis_file_name: l.style_qgis_file_name,
          name: l.name,
          data_source_name: l.data_source_name,
        })
      );
      await Promise.resolve(this.getLayerMetadata({ dataSource: l.data_source_name })).then(value => {
        this.currentLayerStyleEditAttributes = value.metadata.attributes_schema.attributes
          .filter(a => a.data_type.name !== 'geometric')
          .map(a => {
            const isRelational = Boolean(a.relation);
            const dataType = a.data_type;
            const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
            return {
              isRelational,
              valueLabel: isRelational ? `mvt_rel_${a.name}` : a.name,
              valueMvt: isDate ? `mvt_int_${a.name}` : a.name,
              value: a.name,
              text: a.verbose_name,
              dataType,
            };
          });
      });
      this.defaultStyleChanged = true;
      this.isStyleDialogVisible = true;
      this.isStyleDialogLoaded = true;
    },
    async openManageStyleDialog(styleId) {
      const l = this.layer;
      this.styleToEdit = await this.getStyle({ dataSourceName: this.dataSourceName, style_id: styleId });
      this.currentLayerStyleEditGeometryType = l.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(
        JSON.stringify({
          style: this.styleToEdit.style,
          layer_id: l.id,
          style_qgis_file_name: l.style_qgis_file_name,
          name: l.name,
          data_source_name: l.data_source_name,
        })
      );
      const value = await Promise.resolve(this.getLayerMetadata({ dataSource: l.data_source_name }));
      this.currentLayerStyleEditAttributes = value.metadata.attributes_schema.attributes
        .filter(a => a.data_type.name !== 'geometric')
        .map(a => {
          const isRelational = Boolean(a.relation);
          const dataType = a.data_type;
          const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
          return {
            isRelational,
            valueLabel: isRelational ? `mvt_rel_${a.name}` : a.name,
            valueMvt: isDate ? `mvt_int_${a.name}` : a.name,
            value: a.name,
            text: a.verbose_name,
            dataType,
          };
        });
      this.isStyleDialogVisible = true;
      this.isStyleDialogLoaded = true;
    },

    openAddStyleDialog() {
      this.isAddDialogLoaded = true;
      this.isAddDialogVisible = true;
    },
    getStyleName(id) {
      const s = this.styles.find(l => l.id === id);
      return s.name;
    },
    openDeleteDialog(styleId) {
      this.name = this.getStyleName(styleId);
      this.styleToDelete = styleId;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },

    async init() {
      this.isFetched = false;
      await this.getStyles(this.dataSourceName);
      this.isFetched = true;
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.border {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
</style>
